import {SxProps, Theme} from '@mui/material';
import {tabSx} from 'src/components/common/relationshipsPanel.sx';
import theme from 'src/theme';

/**
 * The card is the charger; a section inside it is a connector.
 *
 * It used to be the other way round: one set of figures, one tag and one button at the foot, all of
 * them silently belonging to whichever connector happened to come first. A station with two sockets
 * charging two cars had one reading on screen and no way to tell whose it was. A session belongs to a
 * connector, so the card now repeats itself per connector and shares nothing between them.
 */
export const wrapperSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 2.5,
    py: 1.75,
};

export const badgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** How many sockets are busy, where the card's own status pill used to sit. */
export const countSx: SxProps<Theme> = {
    ml: 'auto',
    fontSize: '0.75rem',
    color: theme.palette.neutral[600],
};

/**
 * The band's tab, holding a label of two lines.
 *
 * The shared tab, with its content pulled to the top left: MUI centres a tab's label, which reads as
 * an accident once the label is a block rather than a word.
 */
export const connectorTabSx: SxProps<Theme> = {
    ...(tabSx as object),
    alignItems: 'flex-start',
    textAlign: 'left',
    py: 1.25,
};

/**
 * The same tab, taking an equal share of the band.
 *
 * Two sockets take half each, three a third. The shared style packs tabs to the left with a right
 * margin between them, which is what has to go once they are measured instead: the margin would come
 * out of one tab's share and not the other's. A hairline divides them, so the split is something you
 * can see rather than infer from where the words happen to start.
 */
export const connectorTabEvenSx: SxProps<Theme> = {
    ...(connectorTabSx as object),
    flex: 1,
    minWidth: 0,
    maxWidth: 'none',
    pr: 2,
    '&:not(:last-of-type)': {mr: 0},
    '&:not(:first-of-type)': {
        pl: 2,
        borderLeft: `1px solid ${theme.palette.neutral[100]}`,
    },
};

/**
 * One tab, naming a socket and saying what it is doing.
 *
 * Stacked: the name and the state are two facts, and side by side they made a tab as wide as the
 * panel below it. The status rides on the tab rather than only inside the panel, because a band
 * shows one connector at a time - without it, a fault on the socket nobody selected is invisible.
 */
export const tabLabelSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.75,
};

/** The first line: which socket, and what kind. */
export const tabHeadSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
};

/** The number people read off the socket and out of the QR code. Filled in while it is in use. */
export const connectorBadgeSx = (inUse: boolean): SxProps<Theme> => ({
    width: '22px',
    height: '22px',
    borderRadius: '6px',
    background: inUse ? theme.palette.primaryColor[100] : theme.palette.neutral[200],
    color: inUse ? theme.palette.primary.dark : theme.palette.neutral[700],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.6875rem',
    fontWeight: 600,
    flexShrink: 0,
});

/** The socket's number, written out. The badge alone is a digit nobody reads as a name. */
export const connectorLabelSx: SxProps<Theme> = {
    fontSize: '0.8125rem',
    fontWeight: 600,
};

/** What it is, after the name: the secondary half of the heading. */
export const connectorTypeSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: theme.palette.neutral[600],
};

export const connectorPowerSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: theme.palette.neutral[600],
};

export const dotSx: SxProps<Theme> = {
    color: theme.palette.neutral[200],
};

export const bodySx: SxProps<Theme> = {
    px: 2.5,
    pt: 1.75,
    pb: 2,
};

/** What the socket is, under the tab that named it. */
export const specRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1.25,
    mb: 1.75,
};

/** The live figures. Only meaningful while a session runs, so they are absent when none does. */
export const figuresRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.75,
};

export const deliveredLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const deliveredValueSx: SxProps<Theme> = {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.025em',
    lineHeight: 1,
    mt: 0.6,
    fontVariantNumeric: 'tabular-nums',
};

export const unitSx: SxProps<Theme> = {
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: theme.palette.neutral[500],
};

export const sideFiguresSx: SxProps<Theme> = {
    textAlign: 'right',
    ml: 'auto',
};

export const sideValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    mt: 0.3,
};

/** Who the energy is billed to, on which card, and what it has cost so far. */
export const metaRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1,
    mt: 1.5,
};

export const metaNameSx: SxProps<Theme> = {
    fontSize: '0.8125rem',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const metaTagSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[600],
    fontVariantNumeric: 'tabular-nums',
};

export const metaCostSx: SxProps<Theme> = {
    ml: 'auto',
    fontSize: '0.875rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
};

export const tagLabelSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[600],
    flexShrink: 0,
};

export const tagValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
};

/**
 * The tag field.
 *
 * Dashed rather than solid: until a card is chosen it is a blank to fill, and the button under it
 * cannot be pressed. One per connector - a single field for the whole card meant the card you picked
 * was spent on whichever socket started first.
 */
export const tagFieldSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    pr: 0.75,
    borderRadius: '9px',
    border: `1px dashed ${theme.palette.primaryColor[100]}`,
    background: theme.palette.primaryColor.A50,
};

/**
 * The field itself is a button, and clearing it is a second one beside it rather than inside it.
 *
 * It used to be a clickable `Box` with a clickable icon nested in it: nothing a keyboard could reach,
 * and an interactive element inside an interactive element.
 */
export const tagPickSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    flex: 1,
    minWidth: 0,
    minHeight: '44px',
    px: 1.5,
    py: 1.1,
    border: 'none',
    borderRadius: '9px',
    background: 'none',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background 150ms ease',
    '&:hover': {background: theme.palette.primaryColor[50]},
};

/**
 * The section's conclusion, and the reason the section exists.
 *
 * Inside the card rather than full-bleed across its foot: a full-width button with square corners
 * reads as the whole card's action, which is exactly the confusion this layout is undoing.
 */
export const sectionButtonSx = (charging: boolean): SxProps<Theme> => ({
    width: '100%',
    minHeight: '44px',
    mt: 1.75,
    borderRadius: '9px',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    textTransform: 'none',
    color: theme.palette.common.white,
    boxShadow: 'none',
    background: charging
        ? `linear-gradient(180deg, ${theme.palette.brand.main} 0%, ${theme.palette.brand.dark} 100%)`
        : `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    '&:hover': {
        boxShadow: 'none',
        background: charging
            ? `linear-gradient(180deg, ${theme.palette.brand.dark} 0%, ${theme.palette.brand.dark} 100%)`
            : `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    },
    '&.Mui-disabled': {
        background: theme.palette.neutral[50],
        color: theme.palette.neutral[400],
    },
});

/**
 * What an idle socket says.
 *
 * A section with nothing running used to render its heading and stop there, so a station at rest was
 * a stack of grey strips - the collapsed list this layout was chosen over. Every connector keeps a
 * body, and an empty one says so.
 */
export const emptySx: SxProps<Theme> = {
    fontSize: '0.8125rem',
    color: theme.palette.neutral[500],
};

/** Why the button above cannot be pressed. Said on the card, not left to a failed request. */
export const hintSx: SxProps<Theme> = {
    mt: 1,
    fontSize: '0.6875rem',
    color: theme.palette.neutral[600],
};
